import React from 'react';
import {
  Truck,
  Shield,
  Clock,
  Headphones,
  CreditCard,
  RefreshCw,
  Stethoscope,
  MapPin,
  Phone,
  CheckCircle
} from 'lucide-react';

const Services = ({ setCurrentPage }) => {
  const mainServices = [
    {
      icon: <Truck className="h-8 w-8" />,
      title: 'توصيل مجاني',
      description: 'توصيل مجاني للطلبات أكثر من 200 ريال داخل المدينة',
      features: ['توصيل خلال ساعتين', 'تتبع الطلب مباشرة', 'تغليف آمن ومحكم'],
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      icon: <Stethoscope className="h-8 w-8" />,
      title: 'استشارة صيدلانية',
      description: 'استشارة مجانية من صيادلة مؤهلين ومعتمدين',
      features: ['متاح 24/7', 'استشارة فورية', 'نصائح طبية موثوقة'],
      color: 'text-[#10B981]',
      bgColor: 'bg-[#10B981]/10'
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: 'ضمان الجودة',
      description: 'جميع منتجاتنا أصلية ومعتمدة من وزارة الصحة',
      features: ['منتجات أصلية 100%', 'تخزين مثالي', 'تواريخ انتهاء طويلة'],
      color: 'text-red-500',
      bgColor: 'bg-red-50'
    },
    {
      icon: <Headphones className="h-8 w-8" />,
      title: 'دعم العملاء',
      description: 'فريق دعم متخصص لمساعدتك في أي وقت',
      features: ['رد سريع', 'دعم متعدد اللغات', 'حلول فعالة'],
      color: 'text-purple-500',
      bgColor: 'bg-purple-50'
    }
  ];

  const additionalServices = [
    { icon: <CreditCard className="h-6 w-6" />, title: 'طرق دفع متعددة', description: 'فيزا، ماستركارد، مدى، أبل باي' },
    { icon: <RefreshCw className="h-6 w-6" />, title: 'سياسة الإرجاع', description: 'إرجاع مجاني خلال 7 أيام' },
    { icon: <Clock className="h-6 w-6" />, title: 'خدمة 24/7', description: 'متاحون في أي وقت لخدمتك' },
    { icon: <MapPin className="h-6 w-6" />, title: 'فروع متعددة', description: '15 فرع في جميع أنحاء المملكة' }
  ];

  return (
    <section className="bg-white py-16 font-['Cairo'] lg:py-24">
      <div className="container mx-auto px-4">
        {/* العنوان الرئيسي */}
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-gray-900 lg:text-4xl">خدماتنا المميزة</h2>
          <p className="mx-auto max-w-2xl text-xl text-gray-600">
            نقدم لك أفضل الخدمات الصحية والطبية بأعلى معايير الجودة والاحترافية
          </p>
        </div>

        {/* الخدمات الرئيسية */}
        <div className="mb-16 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {mainServices.map((service, index) => (
            <div
              key={index}
              className="group rounded-2xl bg-white p-8 shadow-md ring-1 ring-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-[#10B981]/20"
            >
              <div className="flex items-start gap-6">
                <div className={`flex-shrink-0 rounded-2xl ${service.bgColor} p-4 transition-transform duration-300 group-hover:scale-105`}>
                  <div className={service.color}>{service.icon}</div>
                </div>
                <div className="flex-1">
                  <h3 className="mb-3 text-2xl font-bold text-gray-900">{service.title}</h3>
                  <p className="mb-4 leading-relaxed text-gray-600">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2 text-gray-700">
                        <CheckCircle className="h-4 w-4 flex-shrink-0 text-[#10B981]" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* الخدمات الإضافية */}
        <div className="mb-16 rounded-2xl bg-gray-50 p-8 ring-1 ring-gray-100 lg:p-12">
          <h3 className="mb-8 text-center text-2xl font-bold text-gray-900">مزايا إضافية</h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {additionalServices.map((service, index) => (
              <div
                key={index}
                className="group rounded-xl bg-white p-6 text-center shadow-sm ring-1 ring-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:ring-[#10B981]/20"
              >
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#10B981]/10 transition-transform duration-300 group-hover:scale-110">
                  <div className="text-[#10B981]">{service.icon}</div>
                </div>
                <h4 className="mb-2 font-bold text-gray-900">{service.title}</h4>
                <p className="text-sm text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* قسم الاتصال السريع */}
        <div className="rounded-2xl bg-gradient-to-l from-[#10B981] to-indigo-500 p-8 text-white shadow-xl shadow-[#10B981]/10 lg:p-12">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <h3 className="mb-4 text-2xl font-bold lg:text-3xl">هل تحتاج مساعدة فورية؟</h3>
              <p className="mb-6 text-lg opacity-90">
                فريقنا المتخصص متاح على مدار الساعة لتقديم المساعدة والاستشارة الطبية
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <a
                  href="tel:+966111234567"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-4 font-bold text-[#10B981] shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <Phone className="h-5 w-5" />
                  اتصل الآن
                </a>
                <button
                  onClick={() => setCurrentPage && setCurrentPage('consultation')}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/80 px-8 py-4 font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white hover:text-[#10B981]"
                >
                  <Stethoscope className="h-5 w-5" />
                  استشارة فورية
                </button>
              </div>
            </div>
            <div className="text-center lg:text-right">
              <div className="rounded-2xl bg-white/10 p-6 ring-1 ring-white/20 backdrop-blur-sm transition-transform duration-300 hover:scale-[1.02]">
                <div className="mb-4 flex justify-center lg:justify-end">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/15">
                    <Headphones className="h-7 w-7" />
                  </div>
                </div>
                <h4 className="mb-2 text-xl font-bold">خدمة العملاء</h4>
                <p className="text-lg font-semibold">+966 11 123 4567</p>
                <p className="opacity-90">متاح 24/7</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
